/**
 * Axios instances.
 *   • platformApi — auth calls. Sends cookies (withCredentials) and refreshes
 *     once on 401 (mirrors sk-catalog).
 *   • billingApi  — billing calls. Attaches the platform bearer token (from
 *     TokenHandler) + an Idempotency-Key on writes (required by the API).
 *
 * Services use these instances; URLs always come from api.endpoints.tsx.
 */
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { Platform_Refresh_Token_Url, Platform_Token_Url } from './api.endpoints';
import { TokenHandler } from '../storage/tokenHandler';

const NO_TRIM_KEYS = new Set(['password', 'new_password', 'current_password', 'token', 'code', 'secret']);

function trimDeep(value: unknown): unknown {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(trimDeep);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = NO_TRIM_KEYS.has(k) ? v : trimDeep(v);
    return out;
  }
  return value;
}

function genIdempotencyKey(): string {
  // RFC-4122-ish v4; good enough for an idempotency key.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/* ───────────────────────── Platform (auth) ───────────────────────── */
export const platformApi: AxiosInstance = axios.create({
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

platformApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (config.data && typeof config.data === 'object') config.data = trimDeep(config.data);
  return config;
});

let isRefreshing = false;
platformApi.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const url = original?.url || '';
    const skip = /\/auth\/(login|signup|refresh)/.test(url);
    if (error.response?.status === 401 && original && !original._retry && !skip && !isRefreshing) {
      original._retry = true;
      try {
        isRefreshing = true;
        await platformApi.post(Platform_Refresh_Token_Url);
        return platformApi(original);
      } catch (e) {
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  },
);

/* ───────────────────────── Billing ───────────────────────── */
export const billingApi: AxiosInstance = axios.create({
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

billingApi.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (config.data && typeof config.data === 'object') config.data = trimDeep(config.data);

  const token = TokenHandler.getAccessTokenSync() ?? (await TokenHandler.getAccessToken());
  if (token) config.headers.set('Authorization', `Bearer ${token}`);

  const method = (config.method || 'get').toLowerCase();
  if (['post', 'put', 'patch', 'delete'].includes(method) && !config.headers.has('Idempotency-Key')) {
    config.headers.set('Idempotency-Key', genIdempotencyKey());
  }
  return config;
});

/* ── Silent bearer refresh (single-flight), mirroring sk-catalog ────────────
 * The platform access token (Bearer) expires every ~15 min. When a billing
 * call comes back 401, we re-mint the token once via GET /v1/auth/token (which
 * uses the httpOnly session cookie — and platformApi itself refreshes the
 * session on 401), cache it, and replay the original request. The retry re-runs
 * the request interceptor, so it picks up the fresh token automatically.
 */
let billingRefreshInFlight: Promise<string | null> | null = null;

function refreshBillingToken(): Promise<string | null> {
  if (!billingRefreshInFlight) {
    billingRefreshInFlight = platformApi
      .get(Platform_Token_Url)
      .then(({ data }) => {
        const token: string | undefined =
          data?.data?.token ?? data?.data?.access_token ?? data?.access_token ?? data?.token;
        if (token) void TokenHandler.setAccessToken(token);
        return token ?? null;
      })
      .catch(() => null)
      .finally(() => {
        billingRefreshInFlight = null;
      });
  }
  return billingRefreshInFlight;
}

billingApi.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    if (error.response?.status === 401 && original && !original._retried) {
      const token = await refreshBillingToken();
      if (token) {
        original._retried = true;
        return billingApi(original); // re-runs the request interceptor → fresh Bearer
      }
    }
    return Promise.reject(error);
  },
);
